import './styles/tokens.css';
import '@fontsource-variable/dm-sans';
import '@fontsource/dm-mono/400.css';
import '@fontsource/dm-mono/500.css';
import Phaser from 'phaser';
import { BagScene } from './scenes/BagScene';
import { BattleScene } from './scenes/BattleScene';
import { DraftScene } from './scenes/DraftScene';
import { SettingsScene } from './scenes/SettingsScene';
import { EndRunScene } from './scenes/EndRunScene';
import { LearnedMovesModal } from './scenes/LearnedMovesModal';
import { PartySheetScene } from './scenes/PartySheetScene';
import { PokedexScene } from './scenes/PokedexScene';
import { TitleScene } from './scenes/TitleScene';
import { WorldScene } from './scenes/WorldScene';
import {
  DuelSceneFilterPipeline,
  DUEL_FILTER_PIPELINE_KEY,
} from './render/duelSceneFilter';
import { saveManager, installAutoSaveHandlers } from './state/saveManager';
import { seedStore } from './state/seed';
import { careerStore } from './state/careerStore';
import { audio } from './systems/audio';
import { reducedMotion, syncMotionBodyClass } from './render/motion';
import { CANVAS_BG_HEX } from './styles/phaserPalette';
// Side-effect import: registers Oak intercept + starter-ball handlers
// against scriptRunner at module init (PR 13b/d). Must precede any
// scriptRunner.dispatchScript call. No symbols re-exported — the index
// file's top-level registerScript() calls do all the work.
import './scripts/handlers';

// Motion policy (2026-08-02): full animations for everyone — the OS
// prefers-reduced-motion setting is deliberately ignored; the in-app
// Settings "Reduce animations" toggle is the only reduction path.
// Stamp the body class the CSS side keys off (body.motion-reduced)
// before any scene mounts. The legacy singleton stays touched so its
// listener attaches for diagnostics, but no game code reads it.
void reducedMotion;
syncMotionBodyClass();

// Eager-load @pkmn/dex so the lazy chunk is warm by the time DraftScene
// needs nature/ability/learnset data (defensive path (j) in the Pokédex
// design doc). Fire-and-forget; the browser fetches in the background
// while the player navigates from TitleScene through the world. The
// chunk stays lazy (still a dynamic import), so the entry chunk carries
// zero extra weight — only the *timing* shifts forward. By Oak's Lab
// door on a normal connection the chunk is typically resolved; cold
// connections hit DraftScene's loading-state skeleton (Step 5).
void import('@pkmn/dex');

// Phase 10/11 boot order: initialize a fresh master seed FIRST so that any
// code path which rolls RNG before saveManager.load() lands has a working
// seedStore. saveManager.load() will overwrite both runState + seedStore if
// a prior save is found; otherwise the fresh init persists into the first
// auto-save. installAutoSaveHandlers wires pagehide + visibilitychange so
// closing the tab on mobile flushes the snapshot rather than dropping the
// run (which would also drop the rng state and re-randomize on next boot).
seedStore.initFresh();
const loaded = saveManager.load();
if (loaded) console.log('[save] restored prior run');
// Career graveyard lives on its own localStorage key (poke:career); load
// it at boot so PokedexScene's CAREER tab + the DRAFTED-row status JOIN
// have data ready before the first scene mounts. Reset Run does NOT
// touch this — careers persist across runs by design.
careerStore.load();
installAutoSaveHandlers();

// visualViewport returns the *visible* viewport on mobile (excludes the URL
// bar even when present). innerWidth/innerHeight return the layout viewport
// which includes the URL bar — using that on mobile makes the canvas taller
// than the visible area and pushes controls off-screen.
function getVisibleSize(): { width: number; height: number } {
  return {
    width: window.visualViewport?.width ?? window.innerWidth,
    height: window.visualViewport?.height ?? window.innerHeight,
  };
}

const initial = getVisibleSize();

const baseConfig: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game',
  backgroundColor: CANVAS_BG_HEX,
  pixelArt: true,
  scale: {
    mode: Phaser.Scale.RESIZE,
    width: initial.width,
    height: initial.height,
  },
  // Whole-scene duel smoothing filter (DUEL_SCENE_FILTER_ENABLED). Registered
  // globally but only attached to the main camera during a duel, and only
  // when the cycler selects a non-off algorithm — zero cost otherwise. AUTO
  // resolves to WebGL on every target device; on a Canvas fallback the
  // pipeline simply never attaches (guarded at the call site). The cast:
  // PostFX pipelines take a (game) ctor, which Phaser's PipelineConfig type
  // (typed for the (config) WebGLPipeline ctor) doesn't model — accepted at
  // runtime.
  pipeline: {
    [DUEL_FILTER_PIPELINE_KEY]:
      DuelSceneFilterPipeline as unknown as typeof Phaser.Renderer.WebGL.WebGLPipeline,
  },
};

// Forked boot path for the Future Cartridge migration's perf gate.
// When the URL carries ?perftest=1 AND we're in dev or staging mode
// (never production), mount only PerfTestScene to measure
// backdrop-filter throughput on real devices via
//   pnpm build --mode staging && pnpm preview --host 0.0.0.0
// The dynamic import keeps PerfTestScene out of the production bundle
// — Vite splits it into a separate chunk only fetched when the gate
// opens. Production builds run MODE === 'production' so the check
// short-circuits and the chunk is never requested.
const isPerfTest =
  (import.meta.env.MODE === 'staging' || import.meta.env.DEV) &&
  new URLSearchParams(location.search).get('perftest') === '1';

// Same gate, same reason, for the voxel diorama taste gate: boot
// straight into the viewer instead of walking the New Run picker. The
// picker route runs the "your current run will be lost" confirm first
// (nothing is actually cleared unless Begin starts a run — but the
// prompt is a lot of friction to ask of someone who just wants to look
// at the thing on their phone).
const isVoxelPreview =
  (import.meta.env.MODE === 'staging' || import.meta.env.DEV) &&
  new URLSearchParams(location.search).get('voxel') === '1';

async function bootGame(): Promise<Phaser.Game> {
  if (isPerfTest) {
    const { PerfTestScene } = await import('./scenes/PerfTestScene');
    return new Phaser.Game({ ...baseConfig, scene: [PerfTestScene] });
  }
  if (isVoxelPreview) {
    const { VoxelPreviewScene } = await import('./scenes/VoxelPreviewScene');
    return new Phaser.Game({ ...baseConfig, scene: [VoxelPreviewScene] });
  }
  // PartySheetScene registered LAST so it composes ON TOP of every
  // other scene — it launches as a transparent overlay that does NOT
  // sleep WorldScene, so it needs to render above the live world.
  //
  // BagScene is reachable via the popover's Bag row (enabled in bucket
  // G PR 3 alongside the item economy).
  // The scene exists so flipping that disabled flag later is a
  // one-liner; until then this entry is dead weight (~1 KB).
  return new Phaser.Game({
    ...baseConfig,
    scene: [TitleScene, WorldScene, BattleScene, DraftScene, SettingsScene, PokedexScene, BagScene, EndRunScene, PartySheetScene, LearnedMovesModal],
  });
}

// Future Cartridge migration (TODOs #24) — wait for fontsource's
// @font-face declarations (DM Sans Variable, DM Mono 400/500) to
// finish loading before Phaser constructs the game. Phaser snapshots
// font metrics at scene-init from the active font face; without this
// gate, every Text site renders in the system-ui fallback until a
// manual repaint and the page reflows mid-flight. 3-second timeout
// safeguard so a slow/blocked fetch never deadlocks boot.
await Promise.race([
  document.fonts.ready,
  new Promise<void>((resolve) => setTimeout(resolve, 3000)),
]);

const game = await bootGame();

audio.attach(game);

// Phase 15a: pause/resume audio on every "page no longer foreground"
// signal we can hook. iOS Safari is the painful one — different actions
// (power-button sleep, switching apps, locking, viewing lock screen)
// fire different subsets of these events. Cast the widest net:
//
//   visibilitychange hidden — most reliable; tab is not on screen
//   blur                    — window lost focus (desktop mostly)
//   pagehide                — bfcache stash (iOS sleep, Safari back/forward)
//   freeze (Page Lifecycle) — browser intentionally suspended the page
//
// Resume side is intentionally narrow: ONLY visibilitychange visible +
// pageshow + resume + focus. AudioSystem.resumeAll then double-rAFs
// before actually playing so iOS lock-screen-view (which lies about
// visibility but doesn't actually render) leaves audio silent.
const onHide = (): void => audio.pauseAll();
const onShow = (): void => audio.resumeAll();
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') onHide();
  else onShow();
});
window.addEventListener('blur', onHide);
window.addEventListener('focus', onShow);
window.addEventListener('pagehide', onHide);
window.addEventListener('pageshow', onShow);
// Page Lifecycle API. `freeze` fires when the browser frees up the page
// (typically when iOS sleeps the device); `resume` fires on wake.
// Cast through any because TS lib types don't always include these.
document.addEventListener('freeze' as keyof DocumentEventMap, onHide);
document.addEventListener('resume' as keyof DocumentEventMap, onShow);

// iOS Safari + Android Chrome collapse the URL bar without firing
// window.resize — visualViewport.resize is the reliable signal. We don't
// trust scale.refresh() alone (it re-reads the CSS box, which dvh updates
// on its own schedule); explicitly resize() with the freshly-measured
// visible viewport so the canvas + Scene scale.{width,height} are exact.
const syncViewport = (): void => {
  const { width, height } = getVisibleSize();
  game.scale.resize(width, height);
};
window.visualViewport?.addEventListener('resize', syncViewport);
window.addEventListener('resize', syncViewport);
window.addEventListener('orientationchange', syncViewport);

(window as unknown as { __pokeGame?: Phaser.Game }).__pokeGame = game;
